import { useEffect, useMemo, useState } from 'react';

const VIEW_BUTTONS = [
  { overlayKey: 'annotated_summary_url', id: 'all', label: 'All Markers', color: '#6366f1' },
  { overlayKey: 'hair_mask_url', id: 'hair_coverage', label: 'Hair Coverage', color: '#22c55e' },
  { overlayKey: 'contours_url', id: 'hair_strands', label: 'Hair Strands', color: '#16a34a' },
  { overlayKey: 'skeleton_url', id: 'hair_centerlines', label: 'Centerlines', color: '#ef4444' },
  { overlayKey: 'follicle_points_url', id: 'follicle_points', label: 'Follicle Points', color: '#eab308', fallbackKey: 'annotated_summary_url' },
  { overlayKey: 'scalp_gaps_url', id: 'scalp_gaps', label: 'Scalp Gaps', color: '#3b82f6', fallbackKey: 'annotated_summary_url' },
  { overlayKey: 'density_heatmap_url', id: 'density', label: 'Density Map', color: '#f97316' },
  { overlayKey: 'depth_map_url', id: 'depth', label: 'Depth Map', color: '#a855f7' },
  { overlayKey: 'original_url', id: 'original', label: 'Original', color: '#6b7280' },
];

function resolveButtonOverlay(overlays, btn) {
  if (overlays?.[btn.overlayKey]) return btn.overlayKey;
  if (btn.fallbackKey && overlays?.[btn.fallbackKey]) return btn.fallbackKey;
  return null;
}

export default function VisualizationViewer({ overlays, annotations }) {
  const buttons = useMemo(
    () =>
      VIEW_BUTTONS.filter((btn) => resolveButtonOverlay(overlays, btn)).map((btn) => ({
        ...btn,
        resolvedKey: resolveButtonOverlay(overlays, btn),
      })),
    [overlays]
  );

  const [activeId, setActiveId] = useState(buttons[0]?.id || 'all');

  useEffect(() => {
    if (!buttons.length) return;
    if (!buttons.some((b) => b.id === activeId)) {
      setActiveId(buttons[0].id);
    }
  }, [buttons, activeId]);

  if (!buttons.length) return null;

  const activeBtn = buttons.find((b) => b.id === activeId) || buttons[0];
  const imageUrl = overlays[activeBtn.resolvedKey];
  const categories = annotations?.categories || [];

  const getCount = (btn) => {
    if (btn.id === 'all' || btn.id === 'density' || btn.id === 'depth' || btn.id === 'original') {
      return null;
    }
    const cat = categories.find((c) => c.id === btn.id);
    return cat?.count ?? null;
  };

  return (
    <section className="viz-viewer">
      <div className="viz-viewer__image-wrap">
        <img
          src={imageUrl}
          alt={activeBtn.label}
          className="viz-viewer__img"
          loading="lazy"
        />
      </div>

      <div className="viz-viewer__controls">
        {buttons.map((btn) => {
          const count = getCount(btn);
          const isActive = activeId === btn.id;
          return (
            <button
              key={btn.id}
              type="button"
              className={`viz-btn ${isActive ? 'viz-btn--active' : ''}`}
              onClick={() => setActiveId(btn.id)}
            >
              <span className="viz-btn__dot" style={{ backgroundColor: btn.color }} />
              <span className="viz-btn__label">{btn.label}</span>
              {count != null && (
                <span className="viz-btn__count">{count.toLocaleString()}</span>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}
